import { useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartFrame } from '../ChartFrame'
import { useChartSeries } from '../ChartSeriesContext'
import { useChartChrome } from '../cartesianChart'

import { buildNetChangeRows, signedNumber } from './buildNetChangeRows'
import type { NetChangeRow } from './buildNetChangeRows'
import { buildDivergingAxis } from './divergingAxis'
import { NetChangeCategoryTick } from './NetChangeCategoryTick'
import { NetChangeDataTable } from './NetChangeDataTable'
import { NetChangeTooltipView } from './NetChangeTooltipView'

// Layout — a fixed row height with a floor so a one- or two-row chart doesn't
// collapse. Diverging bars centre on zero, so the right margin is modest.
const ROW_HEIGHT = 40
const MIN_HEIGHT = 160
const CHART_VERTICAL_PADDING = 24
const CATEGORY_AXIS_WIDTH = 116
const CHART_MARGIN = { top: 4, bottom: 4, left: 8, right: 48 }
const BAR_RADIUS = 2

const chartHeight = (rowCount: number): number =>
  Math.max(rowCount * ROW_HEIGHT + CHART_VERTICAL_PADDING, MIN_HEIGHT)

interface NetChangeBarShapeProps {
  /** Geometry + data injected by Recharts when used as the Bar `shape`. */
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: NetChangeRow
  /** Resolved colour per series id (from {@link useChartChrome}). */
  colorById?: Record<string, string>
}

/**
 * Per-row bar renderer. A gain/loss draws a rounded rect in the series' own
 * colour; a no-change row draws nothing (its axis label still marks it). Exact
 * values live in the tooltip / data table, so no inline labels are drawn.
 */
function NetChangeBarShape({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  payload,
  colorById = {},
}: NetChangeBarShapeProps): JSX.Element {
  // No movement → no bar.
  if (payload == null || payload.delta === 0) return <g />
  // Recharts Rectangle handles the negative width a loss produces (the bar
  // extends left of zero) and per-corner radius; a raw <rect> with negative
  // width would not render. Gains square their start (left, at zero) and round
  // only the trailing end; losses keep all corners rounded. Colour is applied
  // via CSS `fill` (not the SVG attribute) so it re-themes in dark mode.
  const radius: number | [number, number, number, number] = payload.isGain
    ? [0, BAR_RADIUS, BAR_RADIUS, 0]
    : BAR_RADIUS
  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      radius={radius}
      style={{ fill: colorById[payload.id] }}
    />
  )
}

interface ValueAxisTickProps {
  /** Geometry + tick data injected by Recharts when used as the XAxis `tick`. */
  x?: number
  y?: number
  payload?: { value?: number }
}

/**
 * Bottom value-axis tick. Signed step labels are subtle; the `0` baseline label
 * is bold + dark so it anchors the diverging axis. Fill is set via CSS so it
 * resolves / re-themes in dark mode.
 */
function ValueAxisTick({
  x = 0,
  y = 0,
  payload,
}: ValueAxisTickProps): JSX.Element {
  const value = payload?.value ?? 0
  const isZero = value === 0
  return (
    <text
      x={x}
      y={y}
      dy="0.71em"
      textAnchor="middle"
      fontSize={12}
      style={{
        fill: isZero ? 'var(--color-text-neutral)' : 'var(--color-text-subtle)',
        fontWeight: isZero ? 'bold' : undefined,
      }}
    >
      {signedNumber(value)}
    </text>
  )
}

export interface NetChangeBarChartProps {
  /** Accessible name for the chart (e.g. "Net follower change by channel"). */
  ariaLabel: string
  /** Overlay shown when there's no data; the axes stay visible behind it. */
  emptyState?: ReactNode
  /** Disable enter animations *and* the category-tick hover tooltip — both are
   *  switched off together for the offscreen export snapshot (which renders with
   *  no `TooltipProvider`) and for stable tests. */
  disableAnimations?: boolean
}

/**
 * The base diverging net-change bar chart: one horizontal bar per series showing
 * its net change over the range (gains right, losses left, from a bold zero
 * baseline), sorted by change desc. Series — and their colour/label/icon — come
 * from {@link ChartSeriesContext}; colour is never a prop. Because it plots the
 * *change* rather than an absolute slice, small movements stay visible and
 * comparable across series of very different sizes. Sign is carried by position +
 * the signed value (never by hue), and it's keyboard-navigable with a
 * screen-reader data table via {@link ChartFrame}.
 */
export function NetChangeBarChart({
  ariaLabel,
  emptyState,
  disableAnimations = false,
}: NetChangeBarChartProps): JSX.Element {
  const series = useChartSeries()
  const { resolvedColors, gridColor, neutralTextColor } = useChartChrome(series)

  const rows = useMemo(() => buildNetChangeRows(series), [series])
  const { domain, ticks } = useMemo(
    () => buildDivergingAxis(rows.map((r) => r.delta)),
    [rows],
  )

  return (
    <ChartFrame
      ariaLabel={ariaLabel}
      emptyState={emptyState}
      height={chartHeight(rows.length)}
      dataTable={<NetChangeDataTable caption={ariaLabel} />}
    >
      <BarChart
        data={rows}
        layout="vertical"
        margin={CHART_MARGIN}
        accessibilityLayer
        aria-label={ariaLabel}
      >
        <CartesianGrid horizontal={false} stroke={gridColor} />

        <XAxis
          type="number"
          domain={domain}
          ticks={ticks}
          tick={<ValueAxisTick />}
          axisLine={false}
          tickLine={false}
        />

        <YAxis
          type="category"
          dataKey="id"
          width={CATEGORY_AXIS_WIDTH}
          axisLine={false}
          tickLine={false}
          tick={<NetChangeCategoryTick disableTooltip={disableAnimations} />}
        />

        <Tooltip
          cursor={{ fill: 'transparent' }}
          content={(props) => <NetChangeTooltipView {...props} />}
        />

        <Bar
          dataKey="delta"
          isAnimationActive={!disableAnimations}
          shape={<NetChangeBarShape colorById={resolvedColors} />}
        />

        {/* After <Bar> so the bold baseline draws on top of the bars. */}
        <ReferenceLine x={0} stroke={neutralTextColor} strokeWidth={2} />
      </BarChart>
    </ChartFrame>
  )
}
